import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CampaignType, Template } from '../../types'
import Button from '../../ui/Button'
import Modal, { ModalStateProps } from '../../ui/Modal'
import RadioInput from '../../ui/form/RadioInput'
import { MultiSelect } from '../../ui/form/MultiSelect'
import { SingleSelect } from '../../ui/form/SingleSelect'
import { locales as templateLocales } from './template/TemplateContextProvider'

const hasMultipleVariants = (templates: Template[]): boolean => {
    const countByLocale = new Map<string, number>()
    for (const template of templates) {
        countByLocale.set(template.locale, (countByLocale.get(template.locale) ?? 0) + 1)
    }
    return [...countByLocale.values()].some(count => count > 1)
}

export interface DuplicateCampaignOptions {
    includeVariants: boolean
    locales: string[]
    type: CampaignType
}

interface DuplicateCampaignModalProps extends ModalStateProps {
    templates: Template[]
    campaignType: CampaignType
    onDuplicate: (options: DuplicateCampaignOptions) => Promise<void> | void
}

export const DuplicateCampaignModal = ({ open, onClose, templates, campaignType, onDuplicate }: DuplicateCampaignModalProps) => {
    const { t } = useTranslation()
    const options = useMemo(() => templateLocales(templates), [templates])
    const [selected, setSelected] = useState<string[]>(() => options.map(option => option.key))
    const [mode, setMode] = useState<'all' | 'primary'>('all')
    const [type, setType] = useState<CampaignType>(campaignType)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        if (!open) return
        setSelected(options.map(option => option.key))
        setMode('all')
        setType(campaignType)
    }, [open, options, campaignType])

    const showTemplateOptions = templates.length > 0
    const showVariants = showTemplateOptions && hasMultipleVariants(templates.filter(template => selected.includes(template.locale)))

    const typeOptions: Array<{ key: CampaignType, label: string }> = [
        { key: 'blast', label: t('campaign_type_blast') },
        { key: 'trigger', label: t('campaign_type_trigger') },
    ]

    const handleConfirm = async () => {
        setIsLoading(true)
        try {
            await onDuplicate({
                includeVariants: showVariants ? mode === 'all' : true,
                locales: selected,
                type,
            })
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={t('duplicate_campaign')}
            description={t('duplicate_campaign_description')}
            actions={
                <>
                    <Button variant="secondary" onClick={() => onClose(false)}>{t('cancel')}</Button>
                    <Button
                        isLoading={isLoading}
                        disabled={showTemplateOptions && !selected.length}
                        onClick={handleConfirm}
                    >{t('duplicate')}</Button>
                </>
            }
        >
            <SingleSelect<CampaignType, { key: CampaignType, label: string }>
                label={t('duplicate_campaign_type_label')}
                options={typeOptions}
                toValue={option => option.key}
                getOptionDisplay={option => option.label}
                value={type}
                onChange={setType}
                required
            />
            {showTemplateOptions && (
                <MultiSelect
                    label={t('languages')}
                    subtitle={t('duplicate_campaign_languages_description')}
                    placeholder={t('languages')}
                    options={options}
                    toValue={option => option.key}
                    value={selected}
                    onChange={setSelected}
                    required
                />
            )}
            {showVariants && (
                <RadioInput<'all' | 'primary', { key: 'all' | 'primary', label: string }>
                    label={t('variants')}
                    subtitle={t('duplicate_campaign_variants_description')}
                    value={mode}
                    onChange={setMode}
                    options={[
                        { key: 'all', label: t('duplicate_all_variants') },
                        { key: 'primary', label: t('duplicate_primary_only') },
                    ]}
                />
            )}
        </Modal>
    )
}
